'use client';

import { c as _c } from "react/compiler-runtime";
import { Radio, RadioButton, Skeleton } from '@systemeio/components';
import { useId, type ReactNode } from 'react';
import { FieldLabel } from './form/field-label';
type AccessibleGroupLabel = {
  ariaLabel?: string;
  label: ReactNode;
} | {
  ariaLabel: string;
  label?: never;
};
interface RadioGroupOption<T extends number | string> {
  align?: 'center' | 'start';
  additionalInfo?: ReactNode;
  caption: ReactNode;
  content?: ReactNode;
  disabled?: boolean;
  id: T;
}
type RadioGroupProps<T extends number | string> = AccessibleGroupLabel & {
  disabled?: boolean;
  onChange: (value: T) => void;
  options: readonly RadioGroupOption<T>[] | undefined;
  orientation?: 'horizontal' | 'vertical';
  required?: boolean;
  selected: T | undefined;
};
function RadioGroup(t0) {
  const $ = _c(20);
  const {
    ariaLabel,
    disabled,
    label,
    onChange,
    options,
    orientation: t1,
    required,
    selected
  } = t0;
  const orientation = t1 === undefined ? "horizontal" : t1;
  const groupId = useId();
  const labelId = `${groupId}-label`;
  const groupName = `${groupId}-input`;
  let t2;
  if ($[0] !== label || $[1] !== labelId || $[2] !== required) {
    t2 = label !== undefined ? <FieldLabel label={label} required={required} wrapperProps={{
      className: "flex justify-between gap-2",
      id: labelId
    }} /> : null;
    $[0] = label;
    $[1] = labelId;
    $[2] = required;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = label !== undefined && !ariaLabel ? labelId : undefined;
  const t4 = orientation === "horizontal" ? "flex items-center" : undefined;
  const t5 = selected ?? null;
  let t6;
  if ($[4] !== options || $[5] !== selected) {
    t6 = options ? options.map(option => {
      const radioButton = <RadioButton className={option.align === "center" ? "items-center" : undefined} key={option.id} label={option.additionalInfo ? <span className="flex items-center gap-3">{option.caption}{option.additionalInfo}</span> : option.caption} rootProps={{
        disabled: option.disabled
      }} value={option.id} />;
      if (!option.content) {
        return radioButton;
      }
      return <div className="flex flex-col" key={option.id}>{radioButton}{option.id === selected ? <div className="pt-2">{option.content}</div> : null}</div>;
    }) : <><Skeleton className="size-5 rounded-full" /><Skeleton className="size-5 rounded-full" /></>;
    $[4] = options;
    $[5] = selected;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== ariaLabel || $[8] !== disabled || $[9] !== groupName || $[10] !== onChange || $[11] !== required || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Radio.Group aria-label={ariaLabel} aria-labelledby={t3} className={t4} disabled={disabled} name={groupName} onValueChange={onChange} required={required} value={t5}>{t6}</Radio.Group>;
    $[7] = ariaLabel;
    $[8] = disabled;
    $[9] = groupName;
    $[10] = onChange;
    $[11] = required;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t7) {
    t8 = <div className="flex flex-col gap-2">{t2}{t7}</div>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
export { RadioGroup };
export type { RadioGroupOption, RadioGroupProps };